import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { heroSlides } from "@/data/products";

const EASE = [0.22, 1, 0.36, 1];

export const RotatingHero = () => {
  const [index, setIndex] = useState(0);
  const sectionRef = useRef(null);
  const slide = heroSlides[index];

  const next = useCallback(() => setIndex((i) => (i + 1) % heroSlides.length), []);
  const prev = () => setIndex((i) => (i - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    const t = setTimeout(next, 6800);
    return () => clearTimeout(t);
  }, [index, next]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const circleY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      ref={sectionRef}
      data-testid="rotating-hero"
      className="relative flex items-center overflow-hidden py-16 lg:py-20"
    >
      <motion.div
        style={{ y: circleY }}
        className="pointer-events-none absolute -left-48 bottom-[-8rem] h-[30rem] w-[30rem] rounded-full bg-das-violet/10"
      />
      <div className="pointer-events-none absolute right-[8%] top-[12%] hidden font-display text-[11rem] font-extrabold leading-none text-outline lg:block">
        0{index + 1}
      </div>

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div key={slide.id}>
              <span className="block overflow-hidden">
                <motion.p
                  initial={{ y: "120%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "-120%" }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="text-xs font-bold uppercase tracking-[0.28em] text-das-deep"
                >
                  {slide.overline}
                </motion.p>
              </span>

              <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.04] tracking-tighter text-das-ink sm:text-6xl lg:text-7xl">
                {slide.lines.map((line, i) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span
                      className="block"
                      initial={{ y: "112%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "-112%" }}
                      transition={{ duration: 0.8, delay: 0.08 + i * 0.12, ease: EASE }}
                    >
                      {i === 1 ? (
                        <>
                          {line.split(" ").slice(0, -1).join(" ")}{" "}
                          <span className="italic text-das-violet">{line.split(" ").slice(-1)}</span>
                        </>
                      ) : (
                        line
                      )}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
                className="mt-7 max-w-lg text-base leading-relaxed text-neutral-600 sm:text-lg"
              >
                {slide.copy}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to={slide.cta.to}
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-das-ink px-8 py-4 font-display text-base font-semibold text-white transition-all duration-300 hover:bg-das-deep hover:shadow-lift"
                >
                  {slide.cta.label}
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
                <Link
                  to="/consultation"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-3 rounded-full border border-neutral-300 px-8 py-4 font-display text-base font-semibold text-das-ink transition-all duration-300 hover:border-das-violet hover:text-das-deep"
                >
                  Book a Consultation
                </Link>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-14 flex items-center gap-6">
            <div className="flex gap-3">
              <button
                onClick={prev}
                data-testid="hero-prev-button"
                aria-label="Previous slide"
                className="grid h-12 w-12 place-items-center rounded-full border border-neutral-300 text-das-ink transition-all duration-300 hover:border-das-violet hover:bg-das-violet hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                data-testid="hero-next-button"
                aria-label="Next slide"
                className="grid h-12 w-12 place-items-center rounded-full border border-neutral-300 text-das-ink transition-all duration-300 hover:border-das-violet hover:bg-das-violet hover:text-white"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center gap-2.5">
              {heroSlides.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setIndex(i)}
                  data-testid={`hero-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  className="group relative h-1.5 overflow-hidden rounded-full bg-neutral-200 transition-all duration-500"
                  style={{ width: i === index ? 56 : 24 }}
                >
                  {i === index && (
                    <motion.span
                      key={`progress-${index}`}
                      className="absolute inset-y-0 left-0 rounded-full bg-das-violet"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 6.8, ease: "linear" }}
                    />
                  )}
                </button>
              ))}
            </div>
            <span className="font-display text-sm font-semibold text-neutral-400">
              0{index + 1} / 0{heroSlides.length}
            </span>
          </div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: imageY }} className="relative">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] border border-neutral-200 shadow-ambient">
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={slide.id}
                  src={slide.image}
                  alt={slide.alt}
                  data-testid="hero-slide-image"
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7, ease: EASE }}
              className="absolute -bottom-6 -left-6 rounded-2xl border border-neutral-200 bg-white/85 px-6 py-4 shadow-ambient backdrop-blur-xl"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-das-deep">
                Dynamic App Solutions
              </p>
              <p className="mt-1 font-display text-sm font-semibold text-das-ink">
                For the new generation of business
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
